import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { ConversionData, MbrsStatusEnum } from '../../../types/commons';
import {
  ConversionValidationResult,
  ConversionValidatorService,
} from './conversion-validator.service';
import { ConversionStatusWriteService } from './conversion-status-write.service';

/**
 * AS-842 — Resolve the *display* status of a conversion row by:
 *
 *   1. If the row is not in `PendingValidation`, pass through.
 *   2. If it is, run the silent pure-validator path
 *      (`ConversionValidatorService.validateConversion`), derive the terminal
 *      status from the per-sheet result, POST it back to the backend, and
 *      emit the terminal value.
 *
 * Derivation rule (mirrors §12.1 of the AS-842 design doc):
 *
 *   any sheet `hasErrors`           -> Review
 *   else any sheet `hasSuggestedChanges` -> SuccessWithWarnings (Success*)
 *   else                            -> Success
 *
 * In-flight de-duping: two listing rows / two callers asking for the same
 * conversion concurrently share a single validator + POST cycle. Cleared
 * once the work resolves so a re-conversion (which moves the row through
 * InProgress -> PendingValidation again later) starts a fresh cycle.
 *
 * POST failure handling: the caller still sees the resolved terminal value
 * (so the chip flips correctly for *this* tab). The backend stays at
 * `PendingValidation`, and the next listing reload self-heals via the same
 * resolver. We deliberately do NOT keep retrying inside the resolver —
 * once the user navigates away, the work should stop.
 *
 * Why this is its own service (DIP / SRP):
 *  - Pure derivation function lives next to the orchestrator that already
 *    owns "validate one conversion".
 *  - Listing component depends only on this service, never on the validator
 *    or the HTTP write service directly.
 *  - Replacing the derivation rule (e.g. a future `Success**` token) is a
 *    one-file change.
 */
@Injectable({ providedIn: 'root' })
export class ConversionStatusResolverService {
  private readonly validator = inject(ConversionValidatorService);
  private readonly writer = inject(ConversionStatusWriteService);

  /** conversionId -> in-flight resolution. Shared between concurrent callers. */
  private readonly inFlight = new Map<string, Observable<MbrsStatusEnum>>();

  /**
   * Resolve a single conversion's display status.
   *
   * - Returns `row.status` immediately for any non-`PendingValidation` value.
   * - For `PendingValidation`, returns a cold observable that emits exactly
   *   once with the derived terminal status (after the POST round-trip
   *   resolves; POST failure still emits the derived value).
   */
  resolve(row: ConversionData): Observable<MbrsStatusEnum> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (row.id == "INSERT_MFRS_CONVERSION_ID_HERE" || row.id == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //     console.log("[On Resolve Conversion Status] Row Data: ", row);
    // }

    if (row.status !== MbrsStatusEnum.PendingValidation) {
      return of(row.status);
    }

    const cached = this.inFlight.get(row.id);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (row.id == "INSERT_MFRS_CONVERSION_ID_HERE" || row.id == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //     console.log("[On Resolve Conversion Status | Base Resolve] Cached: ", cached);
    // }

    if (cached) return cached;

    const work$ = this.validator
      // Pass the row's phase so a Phase-1 conversion derives its terminal
      // status from filingPrerequisites alone (the only sheet revealed /
      // carrying data in Phase 1). Without it, the validator's full-registry
      // pass flagged every empty downstream sheet and the row resolved to
      // Review even when Filing Prerequisites was clean.
      .validateConversion(row.id, row.status, row.phase)
      .pipe(
        map((result) => this.deriveTerminal(result)),
        switchMap(
          (terminal) =>
          {
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // if (row.id == "INSERT_MFRS_CONVERSION_ID_HERE" || row.id == "INSERT_MPERS_CONVERSION_ID_HERE") {
            //   console.log("[On Resolve Conversion Status | Base Resolve] Terminal: ", terminal);
            // }
            
            return this.writer.postStatus(row.id, terminal)
              .pipe(
              map(() => terminal),
              catchError(
                  (error) => {
                  // POST failed — keep the derived value displayed locally; row
                  // stays at PendingValidation server-side and will retry on the
                  // next listing reload. Logged for observability.
                  console.warn(
                    '[AS-842] update-conversion-status POST failed for',
                    row.id,
                    error
                  );
                  return of(terminal);
                }
              )
            )
          }
        ),
        finalize(() => {
          this.inFlight.delete(row.id);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.inFlight.set(row.id, work$);
    return work$;
  }

  /**
   * Pure terminal derivation. Public for unit tests; the listing component
   * should not call this directly — go through `resolve()` so the POST
   * write-back happens.
   *
   * Return type is intentionally narrowed to the three terminal values that
   * `ConversionStatusWriteService.postStatus` accepts, so the call-site in
   * `resolve()` type-checks without an assertion.
   *
   * Phase awareness is implicit: for a Phase-1 conversion the validator
   * returns a statuses map containing only `filingPrerequisites`, so the
   * `some(hasErrors)` check below evaluates exactly the product rule —
   * "Review iff Filing Prerequisites has errors, otherwise Success" — with no
   * phase branch needed here.
   */
  deriveTerminal(
    result: ConversionValidationResult
  ):
    | MbrsStatusEnum.Success
    | MbrsStatusEnum.SuccessWithWarnings
    | MbrsStatusEnum.Review {
    // Defensive: when the orchestrator reports `null` statuses (in-progress
    // short-circuit, which we should never reach here because we only call
    // it for PendingValidation), fall back to Review so the row is flagged
    // for human attention rather than silently marked Success.
    if (!result.statuses) return MbrsStatusEnum.Review;

    const sheets = Object.values(result.statuses);
    if (sheets.some((sheet) => sheet.hasErrors)) return MbrsStatusEnum.Review;
    if (sheets.some((sheet) => sheet.hasSuggestedChanges))
      return MbrsStatusEnum.SuccessWithWarnings;
    return MbrsStatusEnum.Success;
  }
}
